// utils/waitlistNotificationService.js - FIXED VERSION
import { useState, useEffect, useCallback } from 'react';
import { db } from '../firebase';
import { 
  collection, 
  query, 
  where, 
  onSnapshot, 
  orderBy, 
  updateDoc, 
  doc, 
  deleteDoc 
} from 'firebase/firestore';

export function useWaitlistNotifications(userId) {
  const [notifications, setNotifications] = useState([]);
  const [hasUnread, setHasUnread] = useState(false);
  const [loading, setLoading] = useState(true);

  // FIXED: Real-time listener for notifications
  useEffect(() => {
    if (!userId) {
      setNotifications([]);
      setHasUnread(false);
      setLoading(false);
      return;
    }

 //   console.log('Setting up waitlist notifications listener for user:', userId);

    const notificationsQuery = query(
      collection(db, 'notifications'),
      where('userId', '==', userId),
      where('type', '==', 'waitlist_promotion'),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(notificationsQuery, (snapshot) => {
      const notificationsList = [];
      const now = new Date();

      snapshot.forEach((doc) => {
        const notification = { id: doc.id, ...doc.data() };
        
        // Check if notification is expired
        const expiresAt = new Date(notification.expiresAt);
        const isExpired = expiresAt <= now;
        
        if (!isExpired) {
          notificationsList.push({
            ...notification,
            expired: false
          });
        } else {
          // Mark expired notifications
          notificationsList.push({
            ...notification,
            expired: true,
            read: true // Auto-mark expired as read
          });
        }
      });

    //  console.log('Waitlist notifications updated:', notificationsList);
      setNotifications(notificationsList);

      // FIXED: Check for unread, non-expired, urgent notifications
      const unreadUrgent = notificationsList.filter(n => 
        !n.read && !n.expired && n.urgent && n.type === 'waitlist_promotion'
      );
      
      const hasUnreadNotifications = unreadUrgent.length > 0;
      setHasUnread(hasUnreadNotifications);

      // FIXED: Dispatch notification update event immediately
      window.dispatchEvent(new CustomEvent('notificationUpdate', {
        detail: { 
          hasUnread: hasUnreadNotifications, 
          count: unreadUrgent.length 
        }
      }));

      setLoading(false);
    }, (error) => {
     // console.error('Error listening to notifications:', error);
      setLoading(false);
    });

    return () => {
     // console.log('Cleaning up notifications listener');
      unsubscribe();
    };
  }, [userId]);

  const markAsRead = useCallback(async (notificationId) => {
    try {
      await updateDoc(doc(db, 'notifications', notificationId), {
        read: true,
        readAt: new Date().toISOString()
      });
     // console.log('Notification marked as read:', notificationId);
      
      // Update local state immediately
      setNotifications(prev => 
        prev.map(n => 
          n.id === notificationId 
            ? { ...n, read: true, readAt: new Date().toISOString() }
            : n
        )
      );
    } catch (error) {
    //  console.error('Error marking notification as read:', error);
    }
  }, []);

  const clearNotification = useCallback(async (notificationId) => {
    try {
      await deleteDoc(doc(db, 'notifications', notificationId));
   //   console.log('Notification cleared:', notificationId);
      
      // Update local state immediately
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
    } catch (error) {
   //   console.error('Error clearing notification:', error);
    }
  }, []);

  const clearAll = useCallback(async () => {
    try {
      const promises = notifications.map(notification => 
        deleteDoc(doc(db, 'notifications', notification.id))
      );
      await Promise.all(promises);
  //    console.log('All notifications cleared');
      
      setNotifications([]);
      setHasUnread(false);
    } catch (error) {
    //  console.error('Error clearing all notifications:', error);
    }
  }, [notifications]);

  return {
    notifications,
    hasUnread,
    loading,
    markAsRead,
    clearNotification,
    clearAll
  };
}